'use client';

import Link from 'next/link';
import { useLibraryStore, isPrimaryLibrary } from '@/stores/library-store';
import type { LibraryConnection } from '@/lib/opds/types';

export function LibraryCard({
  library,
  onEditCredentials,
}: {
  library: LibraryConnection;
  /** Open the credentials editor. Required on the primary card; optional elsewhere. */
  onEditCredentials?: (library: LibraryConnection) => void;
}) {
  const removeLibrary = useLibraryStore((s) => s.removeLibrary);
  const primary = isPrimaryLibrary(library.id);
  const hasAuth = !!library.auth;

  // For the primary library when no credentials are set, the card's
  // primary action is "Sign in" (open the credentials dialog) rather
  // than "open library" — clicking through with no auth would just
  // dead-end at a 401. For every other state the card is a link.
  const needsSignIn = primary && !hasAuth;

  const cardBody = (
    <>
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
        </svg>
      </div>
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-neutral-900">{library.name}</h3>
        {primary && (
          <span
            className="inline-block rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
            title="Default library — URL is fixed and cannot be removed"
          >
            Primary
          </span>
        )}
      </div>
      {/* For the primary library we deliberately don't surface the URL
          (build-time const, the user shouldn't think it's editable).
          For other libraries the URL is part of their identity, so we
          show it. */}
      {!primary && (
        <p className="mt-1 truncate text-sm text-neutral-500">{library.rootUrl}</p>
      )}
      {needsSignIn && (
        <p className="mt-1 text-sm text-amber-700">
          Sign in to start browsing
        </p>
      )}
      {hasAuth && (
        <span className="mt-2 inline-block rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
          Signed in as {library.auth!.username}
        </span>
      )}
    </>
  );

  return (
    <div className="group relative rounded-xl border border-neutral-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      {needsSignIn ? (
        <button
          type="button"
          onClick={() => onEditCredentials?.(library)}
          className="block w-full text-left"
        >
          {cardBody}
        </button>
      ) : (
        <Link href={`/library/${library.id}`} className="block">
          {cardBody}
        </Link>
      )}

      {/* Top-right action cluster: edit-credentials always, remove only
          on non-primary libraries. Both visible on mobile (no hover);
          hover-revealed on desktop to keep the card uncluttered. */}
      <div className="absolute top-3 right-3 flex gap-1 opacity-100 lg:opacity-0 lg:transition-opacity lg:group-hover:opacity-100">
        {onEditCredentials && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onEditCredentials(library);
            }}
            title={hasAuth ? 'Edit credentials' : 'Set credentials'}
            className="rounded-lg p-2 text-neutral-400 hover:bg-blue-50 hover:text-blue-600"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
        {!primary && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              if (confirm(`Remove "${library.name}"?`)) {
                removeLibrary(library.id);
              }
            }}
            title="Remove library"
            className="rounded-lg p-2 text-neutral-400 hover:bg-red-50 hover:text-red-500"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
